import { groupBy, noop } from "lodash-es";

import { times } from "@/math/coordinates";
import type { Board } from "@/model/board";
import { relativeCoord } from "@/model/coordinates";
import type { Art, Team } from "@/model/session";
import { stickyIdHref } from "@/router/navigation";
import { cardSize, useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useSelectionStore } from "@/store/selection";
import { useToastStore } from "@/store/toast";
import { useGlobalI18n } from "@/translations/i18n";
import { alignCards, arrangeCards, distributeCards, lineupCards, stackCards } from "@/utils/layout";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import type { CardCreateProps } from "./addCard";
import { cardActions } from "./cardActions";
import { internalActions } from "./internalActions";
import { linkActions } from "./linkActions";
import { navigationActions } from "./navigationActions";
import {
  cardsInScope,
  execute,
  resetCardPositions,
  saveCardPositions,
  setCardPositions,
  sortByDirection,
  cardPositionState,
  cardPositionChanged,
  alwaysUndoable,
} from "./selectionUtils";
import type { ActionContext } from "./types";

interface MirroringState {
  id: string;
  teamId: string | null;
  mirroredId: string | undefined;
}

export type SelectionScope = "selection" | "region";

const { t } = useGlobalI18n();

export const selectionActions = defineActions("selection", {
  alignHorizontal: ctxAction(alignHorizontal, {
    name: /*$t*/ "action.alignHorizontal",
    icon: "selection/align-horizontal",
  }),
  alignVertical: ctxAction(alignVertical, {
    name: /*$t*/ "action.alignVertical",
    icon: "selection/align-vertical",
  }),
  distributeHorizontal: ctxAction(distributeHorizontal, {
    name: /*$t*/ "action.distributeHorizontal",
    icon: "selection/distribute-horizontal",
  }),
  distributeVertical: ctxAction(distributeVertical, {
    name: /*$t*/ "action.distributeVertical",
    icon: "selection/distribute-vertical",
  }),
  lineup: ctxAction(lineup, { name: /*$t*/ "action.lineup", icon: "selection/grid" }),
  arrange: ctxAction(arrange, { name: /*$t*/ "action.arrange", icon: "selection/arrange" }),
  stack: ctxAction(stack, { name: /*$t*/ "action.stack", icon: "selection/stack" }),
  organizeStickies: untrackedAction(noop, {
    name: /*$t*/ "actionMenu.organize",
    icon: "selection/grid",
  }),
  link: ctxAction(link),
  openMirrorModal: untrackedAction(
    (ctx, scope: SelectionScope) =>
      navigationActions.openModal(ctx, "MirrorModal", { attrs: { scope } }),
    { name: /*$t*/ "action.mirrorMultiple", icon: "general/mirror" },
  ),
  mirror: ctxAction(mirror),
  openTeamModal: untrackedAction(
    (ctx, scope: SelectionScope) =>
      navigationActions.openModal(ctx, "TeamModal", { attrs: { scope } }),
    { name: /*$t*/ "action.team", icon: "persona/team" },
  ),
  setTeam: ctxAction(setTeam),
  openArtModal: untrackedAction(
    (ctx, scope: SelectionScope) =>
      navigationActions.openModal(ctx, "ArtModal", { attrs: { scope } }),
    { name: /*$t*/ "action.art", icon: "general/art" },
  ),
  setArt: ctxAction(setArt),
  paste: untrackedAction(paste),
  duplicate: ctxAction(duplicate),
  share: action(
    (scope: SelectionScope) => {
      const cards = cardsInScope(scope);
      void navigator.clipboard.writeText(
        stickyIdHref(
          cards.map((card) => card.data.id).join(","),
          useBoardStore().currentBoard(),
          "enlarge",
          { noQuery: true },
        ),
      );
      useToastStore().show(/*$t*/ "message.stickyLinkCopied", {
        messageParams: { count: cards.length },
      });
    },
    { name: /*$t*/ "action.share", label: /*$t*/ "label.actionMenu.share", icon: "general/share" },
  ),
  undo: ctxAction(
    (ctx) => {
      ctx.track({ action: useSelectionStore().lastHistoryEntry?.action.name });
      return useSelectionStore().undo();
    },
    { icon: "arrow/undo" },
  ),
});

function alignHorizontal(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) => setCardPositions(alignCards(cards, "horizontal")),
    ...cardPositionState,
  });
}

function alignVertical(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) => setCardPositions(alignCards(cards, "vertical")),
    ...cardPositionState,
  });
}

function distributeHorizontal(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) =>
      setCardPositions(distributeCards(alignCards(cards, "horizontal"), "horizontal")),
    ...cardPositionState,
  });
}

function distributeVertical(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) =>
      setCardPositions(distributeCards(alignCards(cards, "vertical"), "vertical")),
    ...cardPositionState,
  });
}

function lineup(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) => {
      const dist = 0.01 * useClientSettingsStore().initialLineupDistance;
      return setCardPositions(
        lineupCards(cards, cardSize(), dist, (pos) => useBoardStore().boardLocation(pos).bounds),
      );
    },
    ...cardPositionState,
  });
}

function stack(ctx: ActionContext, scope: SelectionScope) {
  const dist = relativeCoord(
    useClientSettingsStore().stackDistanceX,
    useClientSettingsStore().stackDistanceY,
  );
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) =>
      setCardPositions(
        stackCards(
          cards,
          cardSize(),
          times(dist, 0.01),
          (pos) => useBoardStore().boardLocation(pos).bounds,
        ),
        sortByDirection(dist),
      ),
    ...cardPositionState,
  });
}

function arrange(ctx: ActionContext, scope: SelectionScope) {
  return execute(ctx, scope, {
    name: t(ctx.data.name),
    execute: (cards) => {
      const store = useBoardStore();
      const cardsByLoc = groupBy(cards, (card) => store.boardLocation(card.meta.pos).id());
      if (Object.keys(cardsByLoc).length > 1) {
        return useToastStore().show(/*$t*/ "message.multipleRegionsSelected");
      }
      const bounds = store.boardLocationById(Object.keys(cardsByLoc)[0]).freeBounds;
      return setCardPositions(arrangeCards(store.currentBoard(), cards, bounds));
    },
    saveState: (cards) => {
      return {
        factor: useBoardStore().currentBoard().cardSize.factor,
        cards: saveCardPositions(cards),
        length: cards.length,
      };
    },
    resetToState: ({ factor, cards }) => {
      useBoardsStore().setCardSize({ boardId: useBoardStore().currentBoard().id, factor });
      return resetCardPositions(cards);
    },
    stateChanged: (state) => cardPositionChanged(state.cards),
  });
}

function link(ctx: ActionContext, scope: SelectionScope) {
  void execute(ctx, scope, {
    name: cardActions.openLinkModal.data.name,
    execute: noop, // linking is done in LinkModal
    saveState: (cards) =>
      cards.map((card) => ({
        id: card.data.id,
        objectives: [...card.data.objectives],
      })),
    resetToState(cardStates) {
      for (const cardState of cardStates) {
        const card = useCardStore().cards[cardState.id];
        const boardId = useBoardStore().currentBoard().id;
        card.objectives.forEach((newObj) => {
          if (!cardState.objectives.some((oldObj) => oldObj.id === newObj.id)) {
            void linkActions.removeObjective(ctx, card.id, boardId, newObj.id);
          }
        });
        cardState.objectives.forEach((oldObj) => {
          if (!card.objectives.some((newObj) => oldObj.id === newObj.id)) {
            void linkActions.addObjective(ctx, card.id, boardId, oldObj.id);
          }
        });
      }
    },
    ...alwaysUndoable,
  });
}

function mirror(ctx: ActionContext, scope: SelectionScope, board: Board) {
  void execute<MirroringState[]>(ctx, scope, {
    name: selectionActions.openMirrorModal.data.name,
    execute: (_cards, cardStates) => {
      cardStates.forEach(
        (cardState) =>
          void cardActions
            .mirror(ctx, cardState.id, cardState.teamId, board)
            .then((mirroredId) => (cardState.mirroredId = mirroredId)),
      );
    },
    saveState: (cards) =>
      cards.map((card): MirroringState => ({
        id: card.data.id,
        teamId: card.data.teamId,
        mirroredId: undefined,
      })),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        if (cardState.mirroredId) {
          cardActions.delete(ctx, cardState.mirroredId, board.id);
        }
        if (!cardState.teamId) {
          internalActions.setTeam(cardState.id, null);
        }
      });
    },
    ...alwaysUndoable,
  });
}

function setTeam(ctx: ActionContext, scope: SelectionScope, team: Team) {
  void execute(ctx, scope, {
    name: selectionActions.setTeam.data.name,
    execute: (cards) => {
      ctx.track({});
      cards.forEach((card) => cardActions.setTeam(ctx, card.data.id, team.id));
    },
    saveState: (cards) =>
      cards.map((card) => ({
        id: card.data.id,
        teamId: card.data.teamId,
        iterationId: card.data.iterationId,
      })),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => {
        internalActions.setTeam(cardState.id, cardState.teamId);
        internalActions.setIteration(cardState.id, cardState.iterationId);
      });
    },
    ...alwaysUndoable,
  });
}

function setArt(ctx: ActionContext, scope: SelectionScope, art: Art) {
  void execute(ctx, scope, {
    name: selectionActions.setArt.data.name,
    execute: (cards) => {
      ctx.track({});
      cards.forEach((card) => cardActions.setArt(ctx, card.data.id, art.id));
    },
    saveState: (cards) => cards.map((card) => ({ id: card.data.id, artId: card.data.artId })),
    resetToState(cardStates) {
      cardStates.forEach((cardState) => internalActions.setArt(cardState.id, cardState.artId));
    },
    ...alwaysUndoable,
  });
}

// this is a selection action to provide an undo
function paste(ctx: ActionContext, cards: CardCreateProps[]) {
  void execute(ctx, "selection", {
    name: cardActions.pasteMultiple.data.name,
    execute: async (_cards, state) =>
      void state.push(...(await cardActions.pasteMultiple(ctx, cards))),
    saveState: () => new Array<string>(),
    resetToState(cardStates) {
      cardStates.forEach((cardState) =>
        cardActions.delete(ctx, cardState, useBoardStore().currentBoard().id),
      );
    },
    ...alwaysUndoable,
  });
}

function duplicate(ctx: ActionContext, scope: SelectionScope) {
  void execute(ctx, scope, {
    name: cardActions.duplicate.data.name,
    execute: noop, //cards are already duplicated, just register the action for undo
    saveState: (cards) => cards.map((card) => card.data.id),
    resetToState(cardStates) {
      cardStates.forEach((cardState) =>
        cardActions.delete(ctx, cardState, useBoardStore().currentBoard().id),
      );
    },
    ...alwaysUndoable,
  });
}
